import PropTypes from 'prop-types';
import _ from 'underscore';
import React, { useContext, useState } from 'react';
import { SelectionMode } from '@fluentui/react';
import { InsertionOrderSelector } from '@bingads-webui-campaign/reports-v4-component';
import { RestrictionColumnSets } from '@bingads-webui-campaign/reports-v4-schema';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ToolbarContainer } from '@bingads-webui-campaign-react/toolbar-container';
import { ConditionalFormatWrapper } from '@bingads-webui-campaign/conditional-formatting';
import { CampaignShimmeredDetailsList } from '@bingads-webui-grid/campaign-details-list';
import { useReportsHistory } from '@bingads-webui-campaign/reports-v4-history';
import { getToolbarEditor } from '@bingads-webui-campaign-react/toolbar-plugin';
import { getDefaultReportsGridToolbarConfig } from './toolbar-config';
import { FilterViewManagerWrapper } from './filter';
import { AccountSelector } from './account-selector';
import { getScopeSelectorText } from '../helpers/account-selector-helper';

const { ConditionalFormattingRestrictionSets } = RestrictionColumnSets;

const ReportGrid = (props) => {
  const {
    columns, items, reportSchema, onFilterChange, filters, onAccountSelectorChange, accountSelectorInfo, pager,
    onPageChange, onPageSizeChange, onDownload, onSort, conditionalFormats, onConditionalFormatsChange, sortedColumn,
    searchHistorySelectorOptions, onSearchHistorySelectorOptionsChange, perfMarker, actionPerfMarker, shouldHideThisFunctionality,
    onViewItemChange, selectedViewItem, onInsertionOrderSelectorChange, insertionSelectorInfo, hidePagination, groups, groupProps,
    isSearchInsightReportGridModal,
  } = props;

  const i18n = useContext(I18nContext);
  const { scope } = useContext(ScopeContext);
  const permissions = useContext(PermissionsContext);

  const [forceHideToolbar, setForceHideToolbar] = useState(false);
  const history = useReportsHistory();
  const showGroupGrid = !columns.some(column => column.key === 'SearchQuery') && isSearchInsightReportGridModal;
  const isAccountScope = scope.location.search.indexOf('aid') >= 0;

  // eslint-disable-next-line no-undef
  const toolbarConfig = getDefaultReportsGridToolbarConfig({
    reportSchema,
    onDownload,
    scope,
    i18n,
    permissions,
    onViewItemChange,
    selectedViewItem,
  });

  const resetToolbarVisibility = ({ forceHideToolbar: forceHideToolbarPara }) => {
    setForceHideToolbar(forceHideToolbarPara);
  };

  const toolbarEditor = getToolbarEditor({
    toolbarConfig: {
      toolbarSelectedConfig: toolbarConfig,
      toolbarUnselectedConfig: toolbarConfig,
    },
    updateToolbar: undefined,
    overrideWith: undefined,
  });

  toolbarEditor.update({
    id: 'account-selector-toolbar-item',
    content: <AccountSelector
      updateSelected={(selected) => {
        onAccountSelectorChange(selected);
      }}
      searchHistorySelectorOptions={searchHistorySelectorOptions}
      updateSearchHistorySelectorOptions={(newSearchHistorySelectorOptions) => {
        onSearchHistorySelectorOptionsChange(newSearchHistorySelectorOptions);
      }}
      getSelectedScope={() => accountSelectorInfo}
      description={getScopeSelectorText({
        i18n, history, reportSchema, scopeSelectorInfo: accountSelectorInfo,
      })}
      reportSchema={reportSchema}
    />,
  });

  toolbarEditor.update({
    id: 'insertion-order-selector-toolbar-item',
    content: <InsertionOrderSelector
      updateSelectedInsertionOrder={(selected) => {
        onInsertionOrderSelectorChange(selected);
      }}
      selectedInsertionOrders={insertionSelectorInfo}
    />,
  });

  function ToolbarContainerWrapper() {
    return (
      <ToolbarContainer
        i18n={i18n}
        forceHideToolbar={forceHideToolbar}
        showToolbarUnselected
        showToolbarSelected={false}
        toolbarUnselectedConfig={toolbarConfig}
        onClear={() => { }}
        selectionCount={10}
      />
    );
  }

  function getSelectedStatisticsColumns() {
    if (!columns.length || !reportSchema) return [];
    const restrictedColumns = ConditionalFormattingRestrictionSets[reportSchema.ReportType] || [];
    const statisticsCategories = reportSchema.Columns && reportSchema.Columns.categories ? reportSchema.Columns.categories.find(item => item.type === 'MetricColumns') : {};
    if (!statisticsCategories || !statisticsCategories.options) return [];
    const statisticsCategoriesOptions = statisticsCategories.options;
    const StatisticsSet = {};
    statisticsCategoriesOptions.forEach((entry) => {
      StatisticsSet[entry.key] = entry.type;
    });
    const selectedStatisticsColumns = [];
    columns.forEach((item) => {
      if (StatisticsSet[item.className] && !_.contains(restrictedColumns, item.className)) {
        selectedStatisticsColumns.push({ type: StatisticsSet[item.className], ...item });
      }
    });

    return selectedStatisticsColumns;
  }

  const toolbar = (
    <React.Fragment>
      {ToolbarContainerWrapper()}
      {!shouldHideThisFunctionality &&
        <ConditionalFormatWrapper
          toolbarConfig={toolbarConfig}
          conditionalFormats={conditionalFormats || {}}
          onConditionalFormatsChange={onConditionalFormatsChange}
          resetToolbarVisibility={resetToolbarVisibility}
          i18n={i18n}
          toolbarEditor={toolbarEditor}
          conditionalFormatSchema={getSelectedStatisticsColumns()}
        />}
      <FilterViewManagerWrapper
        toolbarEditor={toolbarEditor}
        reportSchema={reportSchema}
        i18n={i18n}
        resetToolbarVisibility={resetToolbarVisibility}
        onFilterChange={onFilterChange}
        filters={filters}
        permissions={permissions}
        isAccountScope={isAccountScope}
      />
    </React.Fragment>
  );

  const searchCategoryGrid = (
    <CampaignShimmeredDetailsList
      stickyHeader
      toolbar={toolbar}
      items={items || []}
      columns={columns}
      selectionMode={SelectionMode.none}
      enableShimmer={!items}
      onSort={column => onSort(column)}
      sortedColumn={sortedColumn}
      freezingColumnCount={1}
      didUpdate={(prev) => {
        if (prev.enableShimmer && items) {
          perfMarker.done();
          if (actionPerfMarker) {
            actionPerfMarker.done();
          }
        }
      }}
      pager={!hidePagination && {
        ...pager,
        onPageChange,
        onPageSizeChange,
      }}
      isSortedDescendingByDefault={false}
      isGridScrollable
      groups={groups}
      groupProps={groupProps}
    />
  );

  const commonReportGrid = (
    <CampaignShimmeredDetailsList
      stickyHeader
      toolbar={toolbar}
      items={items || []}
      columns={columns}
      selectionMode={SelectionMode.none}
      enableShimmer={!items}
      onSort={column => onSort(column)}
      sortedColumn={sortedColumn}
      freezingColumnCount={1}
      didUpdate={(prev) => {
        if (prev.enableShimmer && items) {
          perfMarker.done();
          if (actionPerfMarker) {
            actionPerfMarker.done();
          }
        }
      }}
      pager={!hidePagination && {
        ...pager,
        onPageChange,
        onPageSizeChange,
      }}
      isSortedDescendingByDefault={false}
      isGridScrollable
    />
  );

  return (
    <React.Fragment>
      <div className="report-preview-grid">
        {showGroupGrid && searchCategoryGrid}
        {!showGroupGrid && commonReportGrid}
      </div>
    </React.Fragment >
  );
};

ReportGrid.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  sortedColumn: PropTypes.shape({}).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  reportSchema: PropTypes.shape({
    Columns: PropTypes.arrayOf(PropTypes.shape({
      categories: PropTypes.arrayOf(PropTypes.shape({})),
    })),
    ReportType: PropTypes.string,
  }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  filters: PropTypes.shape({}).isRequired,
  conditionalFormats: PropTypes.shape({}).isRequired,
  onAccountSelectorChange: PropTypes.func.isRequired,
  searchHistorySelectorOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onSearchHistorySelectorOptionsChange: PropTypes.func.isRequired,
  accountSelectorInfo: PropTypes.shape({}).isRequired,
  pager: PropTypes.shape({}).isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  onConditionalFormatsChange: PropTypes.func.isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
  actionPerfMarker: perfMarkerPropTypes.isRequired,
  shouldHideThisFunctionality: PropTypes.bool.isRequired,
  onViewItemChange: PropTypes.func,
  selectedViewItem: PropTypes.string,
  onInsertionOrderSelectorChange: PropTypes.func,
  insertionSelectorInfo: PropTypes.arrayOf(PropTypes.shape({})),
  hidePagination: PropTypes.bool,
  groups: PropTypes.arrayOf(PropTypes.shape({})),
  groupProps: PropTypes.shape({}),
  isSearchInsightReportGridModal: PropTypes.bool,
};

ReportGrid.defaultProps = {
  onViewItemChange: _.noop,
  selectedViewItem: undefined,
  onInsertionOrderSelectorChange: _.noop,
  insertionSelectorInfo: [],
  hidePagination: false,
  groups: undefined,
  groupProps: undefined,
  isSearchInsightReportGridModal: false,
};

export { ReportGrid };
